import React, { useState, useEffect, useMemo } from 'react';
import { useSessionRoster } from '../../../hooks/useSessionRoster';
import { useAuth } from '../../../contexts/AuthContext';
import { RosterPanelHeader } from '../roster';
import RosterMappingModal from '../roster/RosterMappingModal';
import SessionRosterOverviewList from './SessionRosterOverviewList';
import { SessionInfo, telemetryService } from '../../../services/telemetry/telemetryService';

interface SessionRosterPanelProps {
  sessionId?: string | null;
  disableCollapse?: boolean; // Disable collapse functionality (e.g., in dashboard grid)
  expandSignal?: number;
}

const getSeatLabel = (position: number): string => {
  if (position === 1) return 'Seat 1 (Bow)';
  if (position === 8) return 'Seat 8 (Stroke)';
  if (position === 9) return 'Coxswain';
  return `Seat ${position}`;
};

const SessionRosterPanel: React.FC<SessionRosterPanelProps> = ({
  sessionId = null,
  disableCollapse = false,
  expandSignal,
}) => {
  const { user } = useAuth();
  const { data: rosterData = [], isLoading, refetch } = useSessionRoster(sessionId);

  const [isExpanded, setIsExpanded] = useState(false);
  // Always expanded when collapse is disabled
  const effectiveIsExpanded = disableCollapse ? true : isExpanded;
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [sessionInfoLoading, setSessionInfoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const rosterRows = useMemo(
    () =>
      rosterData.map((entry) => ({
        ...entry,
        displayName:
          entry.displayName ||
          `${entry.firstName || ''} ${entry.lastName || ''}`.trim() ||
          (entry.rosterId ? 'Assigned' : null),
        isUnmapped: Boolean(entry.seatId && !entry.rosterId),
      })),
    [rosterData]
  );

  const isReadOnlyDemo = sessionInfo?.isDemo === true;
  const sessionTeamId = sessionInfo?.team_id || undefined;
  const resolvedSessionName = sessionInfo?.sessionName || undefined;

  useEffect(() => {
    let isActive = true;

    const loadSessionInfo = async () => {
      if (!sessionId) {
        setSessionInfo(null);
        return;
      }

      setSessionInfoLoading(true);
      try {
        const sessions = await telemetryService.getSessions();
        if (!isActive) return;
        const matchedSession = sessions.find(
          (s) => s.id === sessionId || s.sessionId === sessionId
        );
        setSessionInfo(matchedSession || null);
      } catch (loadError) {
        if (!isActive) return;
        console.error('Failed to load session metadata for SessionRosterPanel:', loadError);
        setSessionInfo(null);
      } finally {
        if (isActive) {
          setSessionInfoLoading(false);
        }
      }
    };

    loadSessionInfo();

    return () => {
      isActive = false;
    };
  }, [sessionId]);

  useEffect(() => {
    if (expandSignal === undefined) return;
    setIsExpanded(true);
  }, [expandSignal]);

  const handleEditToggle = () => {
    if (isReadOnlyDemo) {
      setError('Demo sessions are read-only.');
      setSuccessMessage(null);
      return;
    }
    setError(null);
    setSuccessMessage(null);
    setIsExpanded(true);
    setIsMappingModalOpen(true);
  };

  // Auto-dismiss success and error messages after 5 seconds
  useEffect(() => {
    if (error || successMessage) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, successMessage, setError, setSuccessMessage]);

  const handleRosterSave = async () => {
    await refetch();
    setError(null);
    setSuccessMessage('Session roster updated.');
    setIsMappingModalOpen(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <RosterPanelHeader
        isEditing={false}
        saving={false}
        isLoading={isLoading}
        masterRosterLoading={sessionInfoLoading}
        sessionId={sessionId}
        user={user}
        isExpanded={isExpanded}
        error={error}
        successMessage={successMessage}
        showSelectionModal={isMappingModalOpen}
        onEditToggle={handleEditToggle}
        onSave={() => {}}
        onCancel={() => {}}
        onToggleExpanded={!disableCollapse ? () => setIsExpanded((prev) => !prev) : undefined}
        disableCollapse={disableCollapse}
        isReadOnlyDemo={isReadOnlyDemo}
      />

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          effectiveIsExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div
          className={`px-3 sm:px-6 py-4 ${disableCollapse ? '' : 'max-h-96 overflow-y-auto custom-scrollbar'}`}
        >
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading roster...</p>
          ) : (
            <SessionRosterOverviewList entries={rosterRows} getSeatLabel={getSeatLabel} />
          )}
        </div>
      </div>

      <RosterMappingModal
        isOpen={isMappingModalOpen && Boolean(sessionId)}
        onClose={() => setIsMappingModalOpen(false)}
        sessionId={sessionId || ''}
        sessionName={resolvedSessionName}
        isDemoSession={isReadOnlyDemo}
        teamId={sessionTeamId}
        onSave={handleRosterSave}
      />
    </div>
  );
};

export default SessionRosterPanel;
